import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CATEGORY_PROGRAM_MAP } from '../utils/vmt';
import { normalizeSelectionBrands } from '../utils/brands';
import { buildCalendarDateMeta, buildDateMeta } from '../utils/dateUtils';

/**
 * Hook used by the send-history tables to backfill missing columns by re-fetching
 * dealer selections. Rows can originate from the send payload (which is sparse),
 * so this hook enriches them to match the ProductSelectionsTable schema.
 */
const FIELDS_TO_FILL = [
  'programCode',
  'programName',
  'brandsDisplay',
  'brandsCodes',
  'vendorName',
  'vendorCode',
  'categoryCode',
  'categoryName',
  'productBrandCode',
  'type',
  'serviceItemName',
  'serviceItemDescription',
  'productSelectionStatus',
  'billingFrequency',
  'gmCost',
  'isBilling',
  'disclaimer',
  'vendorProvidedDate',
  'productSelectionDate',
  'goLiveDate',
  'cancelDate',
  'billingStartDate',
  'billingEndDate',
  'effectiveDate',
  'dealerName',
  'enrollmentStatusCd',
  'isTerminated',
  'terminationDate',
  'enrollmentDate',
  'optOutDate',
];

const FIELD_SOURCE_MAP = {
  programCode: ['programCd', 'program'],
  programName: ['programLabel'],
  vendorName: ['vendor'],
  vendorCode: ['vendorCd'],
  categoryCode: ['categoryCd'],
  categoryName: ['category'],
  productBrandCode: ['productBrandCd', 'productCd'],
  type: ['designation', 'itemType'],
  serviceItemName: ['productDescription', 'serviceItem'],
  serviceItemDescription: ['productDescription', 'serviceItemName'],
  productSelectionStatus: ['status', 'productStatus'],
  billingFrequency: ['occurrence', 'billingFreq'],
  gmCost: ['filler', 'gmFee'],
  dealerPrice: ['price', 'dealerFee'],
  vendorProvidedDate: ['vendorProvidedDt'],
  productSelectionDate: ['selectionDate'],
  goLiveDate: ['effectiveDate'],
  billingStartDate: ['billingDate'],
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim().length === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const deriveProgramName = (row) =>
  row.programName ||
  row.program ||
  CATEGORY_PROGRAM_MAP[row.categoryCd || row.categoryCode || ''] ||
  null;

const normalizeSelections = (payload) => {
  if (!payload) return [];
  const source =
    payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
      ? payload.data
      : payload;
  const containers = [];

  const pushFromEnrollment = (enrollment) => {
    if (!enrollment || !Array.isArray(enrollment.productSelections)) {
      return;
    }
    enrollment.productSelections.forEach((selection) => {
      containers.push({ selection, enrollment });
    });
  };

  if (Array.isArray(source.productSelections)) {
    source.productSelections.forEach((selection) => {
      containers.push({ selection, enrollment: source });
    });
    return containers;
  }

  if (Array.isArray(source.enrollments)) {
    source.enrollments.forEach((enrollment) => pushFromEnrollment(enrollment));
    return containers;
  }

  if (Array.isArray(source.enrollmentReports)) {
    source.enrollmentReports.forEach((enrollment) => pushFromEnrollment(enrollment));
  }

  return containers;
};

const toSelectionContainers = (entries = [], fallbackEnrollment = null) => {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  return entries
    .map((entry) => {
      if (!entry) return null;
      if (entry.selection) {
        return {
          selection: entry.selection,
          enrollment: entry.enrollment ?? fallbackEnrollment ?? null,
        };
      }
      return {
        selection: entry,
        enrollment: fallbackEnrollment ?? entry.enrollment ?? null,
      };
    })
    .filter(Boolean);
};

const normalizeReferenceId = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

const applyOverridesFromSelections = (rowsForCombo, selectionContainers, programName, accumulator) => {
  const wrappedContainers = toSelectionContainers(selectionContainers);
  if (wrappedContainers.length === 0) return;

  const selectionByReference = new Map();
  wrappedContainers.forEach(({ selection, enrollment }) => {
    if (!selection) return;
    const referenceId = normalizeReferenceId(selection.referenceId || selection.referenceID);
    if (!referenceId || selectionByReference.has(referenceId)) return;
    selectionByReference.set(referenceId, { selection, enrollment });
  });

  const getSelectionValue = (selection, field) => {
    const candidateKeys = [field, ...(FIELD_SOURCE_MAP[field] || [])];
    for (const key of candidateKeys) {
      const value = selection[key];
      if (value === undefined || value === null) continue;
      if (typeof value === 'string') {
        if (value.trim().length === 0) continue;
        return value;
      }
      if (Array.isArray(value) && value.length === 0) continue;
      return value;
    }
    return undefined;
  };

  const applyDateOverride = (fieldBase, value, row, overrides, buildMeta = buildDateMeta) => {
    if (!value) return;
    if (isEmptyValue(row[fieldBase]) && isEmptyValue(overrides[fieldBase])) {
      overrides[fieldBase] = value;
    }
    const meta = buildMeta(value);
    const displayField = `${fieldBase}Display`;
    if (isEmptyValue(row[displayField]) && isEmptyValue(overrides[displayField]) && meta.display) {
      overrides[displayField] = meta.display;
    }
    const sortField = `${fieldBase}Sort`;
    if (
      (row[sortField] === null || row[sortField] === undefined) &&
      (overrides[sortField] === null || overrides[sortField] === undefined) &&
      meta.sort !== null &&
      meta.sort !== undefined
    ) {
      overrides[sortField] = meta.sort;
    }
  };

  rowsForCombo.forEach((row) => {
    if (!row) return;
    const referenceId = normalizeReferenceId(row.referenceId || row.referenceID);
    if (!referenceId) return;
    const match = selectionByReference.get(referenceId);
    if (!match) return;
    const { selection, enrollment } = match;

    const brands = normalizeSelectionBrands(
      selection.brands ||
        selection.brandCodes ||
        selection.programBrands ||
        (selection.gmBrandCd ? [selection.gmBrandCd] : [])
    );

    const overridesForRow = { ...(accumulator[row.id] || {}) };
    FIELDS_TO_FILL.forEach((field) => {
      const currentValue = row[field];
      if (!isEmptyValue(currentValue) || !isEmptyValue(overridesForRow[field])) return;
      const nextValue = getSelectionValue(selection, field);
      if (nextValue !== undefined && nextValue !== null) {
        if (
          [
            'vendorProvidedDate',
            'productSelectionDate',
            'goLiveDate',
            'cancelDate',
            'billingStartDate',
            'billingEndDate',
            'effectiveDate',
          ].includes(field)
        ) {
          applyDateOverride(field, nextValue, row, overridesForRow, buildCalendarDateMeta);
        } else {
          overridesForRow[field] = nextValue;
        }
      }
    });

    if (enrollment) {
      if (isEmptyValue(row.dealerName) && isEmptyValue(overridesForRow.dealerName)) {
        overridesForRow.dealerName =
          enrollment.dealerName || enrollment.dealer || enrollment.dealerNm || enrollment.dealerDisplay || '';
      }
      if (isEmptyValue(row.enrollmentStatusCd) && isEmptyValue(overridesForRow.enrollmentStatusCd)) {
        overridesForRow.enrollmentStatusCd =
          enrollment.enrollmentStatusCd || enrollment.programStatusCd || enrollment.status || '';
      }
      if (isEmptyValue(row.isTerminated) && isEmptyValue(overridesForRow.isTerminated)) {
        overridesForRow.isTerminated =
          enrollment.isTerminated ?? enrollment.terminatedFlag ?? enrollment.isTerminatedFlag ?? '';
      }
      applyDateOverride(
        'terminationDate',
        enrollment.isTerminatedDate || enrollment.terminationDate || enrollment.optOutDt || enrollment.optOutDate || '',
        row,
        overridesForRow
      );
      applyDateOverride('enrollmentDate', enrollment.enrolledDt || enrollment.enrollmentDate || '', row, overridesForRow);
      applyDateOverride('optOutDate', enrollment.optOutDt || enrollment.optOutDate || '', row, overridesForRow);
    }

    overridesForRow.programName = overridesForRow.programName || programName;
    overridesForRow.programCode = overridesForRow.programCode || selection.programCode || row.programCode;

    if (isEmptyValue(row.brandsDisplay) && isEmptyValue(overridesForRow.brandsDisplay) && brands.display) {
      overridesForRow.brandsDisplay = brands.display;
    }
    if (
      isEmptyValue(row.brandsCodes) &&
      !overridesForRow.brandsCodes &&
      Array.isArray(brands.codes) &&
      brands.codes.length > 0
    ) {
      overridesForRow.brandsCodes = brands.codes;
    }

    if (Object.keys(overridesForRow).length > 0) {
      accumulator[row.id] = overridesForRow;
    }
  });
};

const applyStoredSnapshotsToRows = (rows, accumulator) => {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const rowsByKey = rows.reduce((acc, row) => {
    const programName = deriveProgramName(row);
    if (!programName || !row?.bac) return acc;
    const hasSnapshots =
      Array.isArray(row?.__sendEntry?.postSendSnapshots) && row.__sendEntry.postSendSnapshots.length > 0;
    if (!hasSnapshots) return acc;
    const key = `${programName}::${row.bac}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  if (Object.keys(rowsByKey).length === 0) return;

  const snapshotCache = {};
  rows.forEach((row) => {
    const entry = row?.__sendEntry;
    if (!entry || !Array.isArray(entry.postSendSnapshots)) return;
    entry.postSendSnapshots
      .filter((snapshot) => snapshot?.status === 'success' && snapshot.enrollment)
      .forEach((snapshot) => {
        const programName = snapshot.programName || deriveProgramName(row);
        const bac = snapshot.bac;
        if (!programName || !bac) return;
        const key = `${programName}::${bac}`;
        const targetRows = rowsByKey[key];
        if (!targetRows || targetRows.length === 0) return;
        if (!snapshotCache[key]) {
          snapshotCache[key] = normalizeSelections(snapshot.enrollment);
        }
        applyOverridesFromSelections(targetRows, snapshotCache[key], programName, accumulator);
      });
  });
};

const getCachedDealerEntry = (queryClient, programName, bac) => {
  if (!programName || !bac) return null;
  const normalizedBac = String(bac).trim();
  const programKey = ['enrollmentData', { type: 'program', value: programName }];
  const cachedProgramRows = queryClient.getQueryData(programKey);
  if (Array.isArray(cachedProgramRows)) {
    const dealer = cachedProgramRows.find((row) => String(row?.bac) === normalizedBac);
    if (dealer) return dealer;
  }
  const allData = queryClient.getQueryData(['allEnrollmentData']);
  if (Array.isArray(allData)) {
    const entry = allData.find((item) => item?.programName === programName);
    if (entry && Array.isArray(entry.data)) {
      const dealer = entry.data.find((row) => String(row?.bac) === normalizedBac);
      if (dealer) return dealer;
    }
  }
  return null;
};

const applyDealerMetaFromCache = (rows, accumulator, queryClient) => {
  rows.forEach((row) => {
    const programName = deriveProgramName(row);
    if (!programName || !row?.bac) return;
    const dealer = getCachedDealerEntry(queryClient, programName, row.bac);
    if (!dealer) return;
    const overridesForRow = { ...(accumulator[row.id] || {}) };
    if (isEmptyValue(row.dealerName) && isEmptyValue(overridesForRow.dealerName) && dealer.dealerName) {
      overridesForRow.dealerName = dealer.dealerName;
    }
    if (
      isEmptyValue(row.enrollmentStatusCd) &&
      isEmptyValue(overridesForRow.enrollmentStatusCd) &&
      dealer.enrollmentStatusCd
    ) {
      overridesForRow.enrollmentStatusCd = dealer.enrollmentStatusCd;
    }
    if (
      isEmptyValue(row.isTerminated) &&
      isEmptyValue(overridesForRow.isTerminated) &&
      dealer.isTerminated !== undefined
    ) {
      overridesForRow.isTerminated = dealer.isTerminated;
    }
    if (Object.keys(overridesForRow).length > 0) {
      accumulator[row.id] = overridesForRow;
    }
  });
};

export const useEnrichedSendHistoryRows = (rows) => {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState({});
  const meta = { usedCache: false };

  useEffect(() => {
    let cancelled = false;
    const updated = {};
    applyStoredSnapshotsToRows(rows, updated);
    applyDealerMetaFromCache(rows, updated, queryClient);
    if (!cancelled) {
      setOverrides(updated);
    }
    return () => {
      cancelled = true;
    };
  }, [rows, queryClient]);

  const enrichedRows = useMemo(
    () =>
      rows.map((row) => {
        if (!row || !overrides[row.id]) return row;
        return { ...row, ...overrides[row.id] };
      }),
    [rows, overrides]
  );

  return { rows: enrichedRows, meta };
};
